import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, Download, FileSignature, Upload, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

/**
 * SPAActions — Download / Sign / Upload-signed Sales & Purchase Agreement
 * Shown on each deal row for both buyer and seller after admin verification.
 */
export default function SPAActions({ deal, onUpdated }) {
  const { getAuthHeader } = useAuth();
  const [signOpen, setSignOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Only available after deal moves past pending_verification
  const ready = deal && deal.status !== 'pending_verification';
  if (!ready) return null;

  const handleDownload = async () => {
    try {
      const res = await axios.get(`${API_URL}/deals/${deal.id}/spa.pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SPA-${deal.id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('SPA downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download SPA');
    }
  };

  const handleSign = async () => {
    if (!agreed) {
      toast.error('Please confirm you agree to the SPA terms');
      return;
    }
    setSigning(true);
    try {
      const res = await axios.post(
        `${API_URL}/deals/${deal.id}/sign`,
        { agreed: true },
        { headers: getAuthHeader() }
      );
      toast.success(
        res.data.fully_executed
          ? 'SPA fully executed — both parties have signed!'
          : 'Your signature recorded. Awaiting counterparty.'
      );
      setSignOpen(false);
      setAgreed(false);
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign');
    } finally {
      setSigning(false);
    }
  };

  const handleUploadSigned = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('File too large (max 8 MB)');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API_URL}/deals/${deal.id}/spa/upload-signed`, fd, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000,
      });
      toast.success('Signed SPA uploaded');
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const userIsSeller = deal.seller_id && deal.buyer_id && deal.seller_id !== deal.buyer_id;
  const hasSignedAsBuyer = Boolean(deal.spa_signed_by_buyer_at);
  const hasSignedAsSeller = Boolean(deal.spa_signed_by_seller_at);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        onClick={handleDownload}
        size="sm"
        data-testid={`spa-download-${deal.id}`}
        className="bg-[#1A2440] text-white border border-[#F59E0B]/40 hover:bg-[#F59E0B]/10"
      >
        <Download className="w-3.5 h-3.5 mr-1" />
        SPA
      </Button>

      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogTrigger asChild>
          <Button
            size="sm"
            data-testid={`spa-sign-${deal.id}`}
            className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1120] font-semibold"
          >
            <FileSignature className="w-3.5 h-3.5 mr-1" />
            Sign
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-[#121A2F] border-[#1E293B] text-white max-w-lg" data-testid="spa-sign-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-[#F59E0B]" />
              Sign Sales & Purchase Agreement
            </DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              Contract <span className="font-mono">GMX-{deal.id.slice(0, 8).toUpperCase()}</span> ·
              Total Value <span className="text-[#F59E0B] font-semibold">${(deal.total_value || 0).toLocaleString()}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-[#0B1120] border border-[#1E293B] rounded-lg p-4 text-sm text-[#94A3B8] space-y-2">
              <p>By clicking &ldquo;I Agree &amp; Sign&rdquo; you confirm that:</p>
              <ul className="list-disc list-inside text-[#94A3B8] space-y-1">
                <li>You are an authorised signatory of the named party</li>
                <li>You have read and understood the SPA in full</li>
                <li>You accept the price, quantity, delivery and payment terms</li>
                <li>This electronic signature is legally binding under clause 14(e)</li>
              </ul>
              <p className="text-xs text-[#64748B] mt-2">
                Your timestamp and IP address will be recorded for evidence.
              </p>
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="spa-agreed"
                checked={agreed}
                onCheckedChange={setAgreed}
                data-testid={`spa-agree-checkbox-${deal.id}`}
                className="mt-1 border-[#F59E0B] data-[state=checked]:bg-[#F59E0B] data-[state=checked]:text-[#0B1120]"
              />
              <label htmlFor="spa-agreed" className="text-sm text-white cursor-pointer">
                I agree to the SPA and confirm my electronic signature.
              </label>
            </div>

            {(hasSignedAsBuyer || hasSignedAsSeller) && (
              <div className="bg-[#10B981]/10 border border-[#10B981]/20 rounded-lg p-3 text-sm text-[#10B981] flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {hasSignedAsBuyer && hasSignedAsSeller
                  ? 'Both parties have signed. SPA fully executed.'
                  : `${hasSignedAsBuyer ? 'Buyer' : 'Seller'} has signed. Awaiting counterparty.`}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              onClick={() => setSignOpen(false)}
              className="bg-transparent border border-[#1E293B] text-[#94A3B8] hover:bg-white/5"
              data-testid={`spa-cancel-${deal.id}`}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSign}
              disabled={!agreed || signing}
              data-testid={`spa-confirm-sign-${deal.id}`}
              className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1120] font-semibold disabled:opacity-50"
            >
              {signing ? 'Signing…' : 'I Agree & Sign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <label
        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md cursor-pointer bg-[#1A2440] border border-[#1E293B] text-[#94A3B8] hover:bg-white/5"
        data-testid={`spa-upload-signed-label-${deal.id}`}
      >
        <Upload className="w-3.5 h-3.5 mr-1" />
        {uploading ? 'Uploading…' : 'Upload Signed'}
        <input
          type="file"
          accept="application/pdf,image/*"
          onChange={handleUploadSigned}
          className="hidden"
          data-testid={`spa-upload-signed-input-${deal.id}`}
          disabled={uploading}
        />
      </label>
    </div>
  );
}
